import React from 'react';
import { Menu } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { TaskyIconButton } from '../button/TaskyIconButton';
import { TaskyHeaderActions } from './TaskyHeaderActions';

/**
 * タイトルの文字スケール。
 *
 * DS 正本（`_docs/ui-redesign/reference/00_design-system.html` の `id="page-header"`）は
 * `text-base font-semibold` だが、現行ページは全て `text-lg`。一斉に縮めるとデザインの
 * 判断が要るため、いまは現状の `text-lg` を維持する。判断がついたらこの 1 行を
 * 変えるだけで共通バーを使う全ページが切り替わる。
 */
const TOP_BAR_TITLE_CLASS = 'm-0 truncate text-lg font-semibold text-dark';

const EXPAND_SIDEBAR_LABEL = 'サイドバーを展開';

export interface TaskyTopBarProps {
  title: React.ReactNode;
  /** タイトル左の lucide アイコン（任意）。色は部品側で primary を当てる */
  icon?: React.ReactNode;
  /** アイコンより前に置く要素（戻るボタン・セレクタなど） */
  leading?: React.ReactNode;
  /** h1 の直後に置く補足（説明 span・件数など） */
  titleAddon?: React.ReactNode;
  /** true のとき、サイドバーの展開ボタンを出す */
  sidebarCollapsed?: boolean;
  onExpandSidebar?: () => void;
  /** 既定 true。右端に <TaskyHeaderActions /> を出す */
  showHeaderActions?: boolean;
  /** 右側のページ固有の操作（ピルの左に並ぶ） */
  children?: React.ReactNode;
  className?: string;
}

/**
 * デスクトップ用の共通ヘッダバー（56px）。
 *
 * 右端にヘルプ・クレジット残高のピルを常設し、ページ固有の操作はその左に並べる。
 * これにより App の固定オーバーレイが操作要素に被らない。`hidden lg:flex` なのは
 * 既存の手書きバーと同じで、モバイルは ResponsiveAppShell のトップバーが担う。
 */
export const TaskyTopBar: React.FC<TaskyTopBarProps> = ({
  title,
  icon,
  leading,
  titleAddon,
  sidebarCollapsed,
  onExpandSidebar,
  showHeaderActions = true,
  children,
  className,
}) => (
  <header
    className={cn(
      'hidden h-14 shrink-0 items-center justify-between gap-3 border-b border-warm-border bg-white px-4 lg:flex',
      className
    )}
  >
    <div className="flex min-w-0 items-center gap-3">
      {sidebarCollapsed && onExpandSidebar && (
        <TaskyIconButton
          type="button"
          size="sm"
          variant="ghost"
          aria-label={EXPAND_SIDEBAR_LABEL}
          title={EXPAND_SIDEBAR_LABEL}
          onClick={onExpandSidebar}
        >
          <Menu className="h-4 w-4" />
        </TaskyIconButton>
      )}
      {leading}
      {icon && <span className="inline-flex shrink-0 text-primary">{icon}</span>}
      <h1 className={TOP_BAR_TITLE_CLASS}>{title}</h1>
      {titleAddon}
    </div>
    <div className="flex shrink-0 items-center gap-2">
      {children}
      {showHeaderActions && <TaskyHeaderActions />}
    </div>
  </header>
);
